/**
 * @file Button.jsx
 * @description Core button component for Inspection Butler.
 *
 * @param {'primary'|'secondary'|'outline'|'ghost'|'danger'|'success'} [variant='primary']
 * @param {'sm'|'md'|'lg'|'xl'} [size='md']
 * @param {boolean} [loading=false] - Show spinner, disable interaction
 * @param {boolean} [disabled=false]
 * @param {boolean} [fullWidth=false]
 * @param {React.ReactNode} [leftIcon] - Icon element to the left of text
 * @param {React.ReactNode} [rightIcon] - Icon element to the right of text
 * @param {string} [as='button'] - Render as 'button', 'a', or a React Router Link
 * @param {string} [className]
 * @param {string} [aria-label]
 * @param {React.ReactNode} children
 */
import React, { useRef, useCallback } from 'react';

const styles = {
  base: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-2)',
    fontFamily: 'var(--font-family)',
    fontWeight: 'var(--weight-semibold)',
    lineHeight: 1,
    border: '2px solid transparent',
    borderRadius: 'var(--radius-lg)',
    cursor: 'pointer',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    transition: `all var(--transition-fast) var(--ease-default)`,
    overflow: 'hidden',
    outline: 'none',
  },

  /* Ensure minimum touch target regardless of visual size */
  touchTarget: {
    position: 'relative',
  },

  /* Size variants */
  sizes: {
    sm: {
      minHeight: 'var(--btn-height-sm)',
      padding: '0 var(--space-3)',
      fontSize: 'var(--text-body-sm)',
    },
    md: {
      minHeight: 'var(--btn-height-md)',
      padding: '0 var(--space-4)',
      fontSize: 'var(--text-body-md)',
    },
    lg: {
      minHeight: 'var(--btn-height-lg)',
      padding: '0 var(--space-5)',
      fontSize: 'var(--text-body-md)',
    },
    xl: {
      minHeight: 'var(--btn-height-xl)',
      padding: '0 var(--space-6)',
      fontSize: 'var(--text-body-lg)',
      borderRadius: 'var(--radius-xl)',
    },
  },

  /* Color variants */
  variants: {
    primary: {
      backgroundColor: 'var(--color-primary)',
      color: '#FFFFFF',
      borderColor: 'var(--color-primary)',
    },
    secondary: {
      backgroundColor: 'var(--surface-sunken)',
      color: 'var(--text-primary)',
      borderColor: 'var(--border-default)',
    },
    outline: {
      backgroundColor: 'transparent',
      color: 'var(--color-primary)',
      borderColor: 'var(--color-primary)',
    },
    ghost: {
      backgroundColor: 'transparent',
      color: 'var(--text-primary)',
      borderColor: 'transparent',
    },
    danger: {
      backgroundColor: 'var(--color-error)',
      color: '#FFFFFF',
      borderColor: 'var(--color-error)',
    },
    success: {
      backgroundColor: 'var(--color-success)',
      color: '#FFFFFF',
      borderColor: 'var(--color-success)',
    },
  },

  hoverVariants: {
    primary:   { backgroundColor: 'var(--color-primary-hover)' },
    secondary: { backgroundColor: 'var(--color-gray-100)' },
    outline:   { backgroundColor: 'var(--color-primary-subtle)' },
    ghost:     { backgroundColor: 'var(--overlay-light)' },
    danger:    { backgroundColor: 'var(--color-error-700)' },
    success:   { backgroundColor: 'var(--color-success-700)' },
  },

  disabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },

  fullWidth: {
    width: '100%',
  },

  spinnerWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  hiddenText: {
    visibility: 'hidden',
  },

  loadingOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

/* Inline keyframes for the spinner — avoids needing a CSS file */
const spinnerKeyframes = `
@keyframes ib-btn-spin {
  to { transform: rotate(360deg); }
}
`;

/** Spinner SVG for loading state */
function Spinner({ size = 18, color = 'currentColor' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      style={{ animation: 'ib-btn-spin 0.7s linear infinite' }}
      aria-hidden="true"
    >
      <circle
        cx="12" cy="12" r="10"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="31.4 31.4"
        strokeDashoffset="10"
      />
    </svg>
  );
}

/** Ripple effect element */
function createRipple(e, containerEl) {
  const rect = containerEl.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height) * 2;
  const x = e.clientX - rect.left - size / 2;
  const y = e.clientY - rect.top - size / 2;

  const ripple = document.createElement('span');
  ripple.style.cssText = `
    position: absolute;
    width: ${size}px;
    height: ${size}px;
    left: ${x}px;
    top: ${y}px;
    background: currentColor;
    opacity: 0.12;
    border-radius: 50%;
    transform: scale(0);
    pointer-events: none;
    animation: ib-ripple 0.5s ease-out forwards;
  `;
  containerEl.appendChild(ripple);
  setTimeout(() => ripple.remove(), 550);
}

const rippleKeyframes = `
@keyframes ib-ripple {
  to { transform: scale(1); opacity: 0; }
}
`;

/* Inject keyframes once */
let keyframesInjected = false;
function injectKeyframes() {
  if (keyframesInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = spinnerKeyframes + rippleKeyframes;
  document.head.appendChild(style);
  keyframesInjected = true;
}

const Button = React.forwardRef(function Button(props, ref) {
  const {
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    fullWidth = false,
    leftIcon,
    rightIcon,
    as: Component = 'button',
    className = '',
    children,
    onClick,
    style: userStyle,
    ...rest
  } = props;

  const innerRef = useRef(null);
  const btnRef = ref || innerRef;

  // Inject keyframes on first render
  React.useEffect(() => { injectKeyframes(); }, []);

  const [hovered, setHovered] = React.useState(false);

  const isDisabled = disabled || loading;

  const handleClick = useCallback((e) => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }
    // Ripple
    if (btnRef.current) {
      createRipple(e, btnRef.current);
    }
    if (onClick) onClick(e);
  }, [isDisabled, onClick, btnRef]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.currentTarget.click();
    }
  }, []);

  const computedStyle = {
    ...styles.base,
    ...styles.sizes[size] || styles.sizes.md,
    ...styles.variants[variant] || styles.variants.primary,
    ...(hovered && !isDisabled ? (styles.hoverVariants[variant] || {}) : {}),
    ...(isDisabled ? styles.disabled : {}),
    ...(fullWidth ? styles.fullWidth : {}),
    /* Invisible padding for touch target — ensure 48x48 minimum */
    minWidth: 'var(--touch-min)',
    ...userStyle,
  };

  const buttonProps = {
    ref: btnRef,
    className: `ib-btn ib-btn--${variant} ib-btn--${size} ${className}`.trim(),
    style: computedStyle,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    disabled: Component === 'button' ? isDisabled : undefined,
    'aria-disabled': isDisabled || undefined,
    'aria-busy': loading || undefined,
    role: Component !== 'button' ? 'button' : undefined,
    tabIndex: isDisabled ? -1 : 0,
    ...rest,
  };

  return (
    <Component {...buttonProps}>
      {loading ? (
        <>
          <span style={styles.hiddenText} aria-hidden="true">
            {leftIcon && <span>{leftIcon}</span>}
            {children}
            {rightIcon && <span>{rightIcon}</span>}
          </span>
          <span style={styles.loadingOverlay}>
            <Spinner size={size === 'sm' ? 14 : size === 'xl' ? 22 : 18} />
          </span>
        </>
      ) : (
        <>
          {leftIcon && <span style={{ display: 'inline-flex', flexShrink: 0 }}>{leftIcon}</span>}
          {children}
          {rightIcon && <span style={{ display: 'inline-flex', flexShrink: 0 }}>{rightIcon}</span>}
        </>
      )}
    </Component>
  );
});

Button.displayName = 'Button';

export default Button;
